import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import {
  Flex as DsFlex,
  Text,
  Box,
  TextArea as DsTextArea,
  DropdownMenu,
  IconButton,
} from 'components/ds'
import Button from 'components/ui/button'
import {
  createBriefNote,
  updateBriefNote,
  deleteBriefNote,
  getBriefNotesForBrief,
  getBriefNotesForProject,
} from '@disco/state-management/reducers/entities/briefNotes'
import { loadBriefProject } from '@disco/state-management/reducers/entities/briefProjects'
import {
  getBrief,
  loadBrief,
} from '@disco/state-management/reducers/entities/briefs'
import type { BriefNote } from 'types/brief'
import Icon from 'components/ui/icon'
import { formatDate } from '@disco/utils/time-utils'
import { getUserDisplayName } from '@disco/utils/string-utils'
import styles from './styles.module.css'
import mixpanel from '@disco/api/mixpanel'

interface BriefNotesProps {
  briefProjectId?: string
  briefId?: string
  handleCancel?: () => void
}

const buildNotePayload = (
  noteText: string,
  briefProjectId?: string,
  briefId?: string,
  handleCancel?: () => void,
) => {
  const payload: any = {
    note_text: noteText.trim(),
  }

  if (briefProjectId) {
    payload.for_project = Number(briefProjectId)
  } else if (briefId) {
    payload.for_brief = Number(briefId)
  }

  return payload
}

const BriefNotes: React.FC<BriefNotesProps> = ({
  briefProjectId,
  briefId,
  handleCancel,
}) => {
  const dispatch = useDispatch()
  const [noteText, setNoteText] = useState('')
  const [isCreatingNote, setIsCreatingNote] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null)
  const [editingNoteText, setEditingNoteText] = useState('')

  const brief = useSelector(state =>
    getBrief(state, { briefId: parseInt(briefId) }),
  )

  // Load brief/project when component mounts to populate notes
  useEffect(() => {
    if (briefProjectId) {
      dispatch(loadBriefProject(Number(briefProjectId)))
    } else if (briefId && !brief?.associated_notes) {
      dispatch(loadBrief(Number(briefId)))
    }
  }, [dispatch, briefProjectId, briefId, brief?.associated_notes])

  // Get notes from briefNotes store (already sorted by id, most recent last)
  const notes = useSelector(state => {
    if (briefProjectId) {
      return getBriefNotesForProject(state, {
        briefProjectId: Number(briefProjectId),
      })
    } else if (briefId) {
      return getBriefNotesForBrief(state, { briefId: Number(briefId) })
    }
    return []
  })

  const handleAddNote = async () => {
    if (!noteText.trim() || (!briefProjectId && !briefId)) return

    setIsCreatingNote(true)
    try {
      await dispatch(
        createBriefNote(buildNotePayload(noteText, briefProjectId, briefId)),
      ).unwrap()
      mixpanel.sendPureEvent('Brief note added', {
        Context: 'Briefs',
        brief_id: briefId,
      })
      setNoteText('')
    } catch (error) {
      // Error is handled by Redux action
    } finally {
      setIsCreatingNote(false)
    }
  }

  const handleEditNote = (note: BriefNote) => {
    setEditingNoteId(note.id)
    setEditingNoteText(note.note_text)
  }

  const handleCancelEdit = () => {
    setEditingNoteId(null)
    setEditingNoteText('')
  }

  const handleUpdateNote = async (noteId: number) => {
    if (!editingNoteText.trim() || (!briefProjectId && !briefId)) return

    try {
      await dispatch(
        updateBriefNote({
          noteId,
          payload: buildNotePayload(editingNoteText, briefProjectId, briefId),
        }),
      ).unwrap()
      mixpanel.sendPureEvent('Brief note edited', {
        Context: 'Briefs',
        brief_id: briefId,
      })
      setEditingNoteId(null)
      setEditingNoteText('')
    } catch (error) {
      // Error is handled by Redux action
    }
  }

  const handleDeleteNote = async (noteId: number) => {
    try {
      await dispatch(deleteBriefNote(noteId)).unwrap()
      mixpanel.sendPureEvent('Brief note deleted', {
        Context: 'Briefs',
        brief_id: briefId,
      })
      setEditingNoteId(null)
      setEditingNoteText('')
    } catch (error) {
      // Error is handled by Redux action
    }
  }

  return (
    <DsFlex
      direction="column"
      gap="3"
      height="100%"
      className={styles.notesContainer}
    >
      {/* Existing Notes */}
      {notes.length > 0 && (
        <Box flexGrow="1" className={styles.notesListContainer}>
          <DsFlex direction="column" gap="2">
            {notes.map(note => (
              <Box key={note.id} p="3" className={styles.noteCard}>
                {editingNoteId === note.id ? (
                  <DsFlex direction="column" gap="3">
                    <DsTextArea
                      size="2"
                      value={editingNoteText}
                      onChange={e => setEditingNoteText(e.target.value)}
                      className={styles.editingTextarea}
                    />
                    <DsFlex gap="2" justify="end">
                      <Button
                        appearance="subtle"
                        caption="Cancel"
                        onClick={handleCancelEdit}
                      />
                      <Button
                        appearance="primary"
                        caption="Save note"
                        progressCaption="Saving note"
                        progressIndicator={note.updating}
                        onClick={() => handleUpdateNote(note.id)}
                        disabled={!editingNoteText.trim()}
                      />
                    </DsFlex>
                  </DsFlex>
                ) : (
                  <DsFlex direction="column" gap="2">
                    <DsFlex justify="between" align="start">
                      <Text size="2" className={styles.noteText}>
                        {note.note_text}
                      </Text>
                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger>
                          <IconButton
                            variant="ghost"
                            size="1"
                            className={styles.menuButton}
                          >
                            <Icon
                              type="ellipsis-vertical"
                              width="15"
                              height="15"
                            />
                          </IconButton>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content>
                          <DropdownMenu.Item
                            onClick={() => handleEditNote(note)}
                          >
                            Edit
                          </DropdownMenu.Item>
                          <DropdownMenu.Item
                            color="red"
                            onClick={() => handleDeleteNote(note.id)}
                          >
                            Delete
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Root>
                    </DsFlex>
                    <Text size="1" color="gray" align="right">
                      {note.modified_by ? (
                        <>
                          edited by {getUserDisplayName(note.modified_by)} on{' '}
                          {formatDate(note.modified)}
                        </>
                      ) : (
                        <>
                          created by {getUserDisplayName(note.created_by)} on{' '}
                          {formatDate(note.created)}
                        </>
                      )}
                    </Text>
                  </DsFlex>
                )}
              </Box>
            ))}
          </DsFlex>
        </Box>
      )}

      {/* Internal Notes Section */}
      <DsFlex flexShrink="0" gap="3" direction="column" mt="1">
        <Text size="2" weight="medium">
          Internal Notes
        </Text>

        <DsTextArea
          size="2"
          placeholder="Add a note..."
          value={noteText}
          onChange={e => setNoteText(e.target.value)}
          disabled={isCreatingNote}
          className={styles.noteTextarea}
        />

        {/* Add Note Button */}
        <DsFlex gap="3" mt="2" justify="end">
          {handleCancel && (
            <Button
              appearance="subtle"
              caption="Cancel"
              onClick={handleCancel}
            />
          )}
          <Button
            appearance="primary"
            caption="Add note"
            progressCaption="Adding note"
            progressIndicator={isCreatingNote}
            onClick={handleAddNote}
            disabled={!noteText.trim()}
          />
        </DsFlex>
      </DsFlex>
    </DsFlex>
  )
}

export default BriefNotes
